import React, { ReactNode } from "react";
import { Box, Flex, Progress, Text } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { StartGuideStep } from "@/features/pathpick/features/start-guide/types";
import { getStartGuideStepLabelKey } from "@/features/pathpick/features/start-guide/constants";

interface Props {
  // 지금 판매 방식에서 실제로 보여 줄 단계들 — KREAM 전용이면 `상품 담기`가 빠진다.
  steps: readonly StartGuideStep[];
  isKreamOnly: boolean;
  currentStep: StartGuideStep;
  // 0~1 진행률 — 화면에는 안 보이고 스크린리더에만 읽힌다.
  progressValue: number;
  onChange: (step: StartGuideStep) => void;
  // 탭 줄 우측 끝에 붙는 보조 액션 — 판매 방식 변경·테스트값 초기화.
  rightSlot?: ReactNode;
}

/**
 * 상단 단계 탭 — 작고 눈에 띄지 않는 텍스트 탭 한 줄.
 *
 * 활성 표시는 파란 글자와 하단 1px 선 뿐이다. 큰 단계 카드나 진행률 블록은 쓰지 않는다(전달서 §4).
 * 모든 탭은 언제든 누를 수 있다 — 앞 단계를 끝내지 않아도 뒤로 건너뛸 수 있어야 한다.
 */
function PathPickStartGuideStepTabs({
  steps,
  isKreamOnly,
  currentStep,
  progressValue,
  onChange,
  rightSlot,
}: Props) {
  const { t } = useI18nHelper();

  return (
    <>
      {/* 시각적으로는 탭이 진행을 보여주지만, 스크린리더에는 진행률도 함께 알린다. */}
      <Progress
        aria-label={t("path_pick_guide:tabs.progress_aria_label")}
        value={progressValue * 100}
        height={"1px"}
        opacity={0}
        position={"absolute"}
        pointerEvents={"none"}
      />
      {/* 밑선은 탭과 보조 액션을 함께 감싸는 이 줄이 진다 — 탭만 지면 우측에서 선이 끊긴다. */}
      <Flex
        alignItems={"flex-end"}
        gap={"16px"}
        borderBottom={"0.5px solid #E5E5E3"}
      >
        <Flex
          as={"nav"}
          aria-label={t("path_pick_guide:tabs.aria_label")}
          gap={"16px"}
          flexGrow={1}
          minWidth={0}
          // 모바일에서는 가로로 흐르게 두되 글자는 줄이지 않는다(전달서 §4).
          overflowX={"auto"}
          sx={{
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {steps.map((step) => {
            const isActive = step === currentStep;
            return (
              <Box
                key={step}
                as={"button"}
                type={"button"}
                onClick={() => onChange(step)}
                aria-current={isActive ? "step" : undefined}
                aria-label={t("path_pick_guide:tabs.item_aria_label", {
                  title: t(getStartGuideStepLabelKey(step, isKreamOnly)),
                })}
                flexShrink={0}
                paddingBottom={"8px"}
                // 활성 탭만 하단 1px 선. 비활성도 같은 높이를 차지해야 눌러도 안 흔들린다.
                borderBottom={`1px solid ${
                  isActive ? "#1272EF" : "transparent"
                }`}
                marginBottom={"-0.5px"}
              >
                <Text
                  textStyle={isActive ? "body-sm-bold" : "body-sm-medium"}
                  color={isActive ? "#1272EF" : "#6F6F6C"}
                  whiteSpace={"nowrap"}
                  _hover={{ color: isActive ? "#1272EF" : "#22221F" }}
                >
                  {t(getStartGuideStepLabelKey(step, isKreamOnly))}
                </Text>
              </Box>
            );
          })}
        </Flex>
        {rightSlot && (
          <Flex
            flexShrink={0}
            alignItems={"center"}
            gap={"12px"}
            paddingBottom={"8px"}
          >
            {rightSlot}
          </Flex>
        )}
      </Flex>
    </>
  );
}

export default PathPickStartGuideStepTabs;
